/**
 * Global registry of the forms that currently hold unsaved changes (KP-2453).
 *
 * Deliberately NOT a React context: `HeaderSelect` lives outside the MerchantSetting
 * subtree (Sidebar/Header are siblings of the page inside DefaultLayout), so it cannot
 * read a context mounted within the page. A module singleton lets any component import
 * `requestLeave` directly without wrapping a Provider higher up the tree.
 *
 * The store is inert while no form is registered: `requestLeave` calls `proceed()` at once.
 */

/** A mounted form describing how to inspect, save and restore its own changes. */
export interface UnsavedChangesSource {
  /** Unique key of the mounted form, usually the tab key. */
  id: string;
  /** Names of the fields differing from the original values. Empty array means clean. Must be synchronous because `beforeunload` needs the result immediately. */
  getChangedFields: () => string[];
  /** Validate and save. true when it succeeded, false on validation failure or API error. */
  save: () => Promise<boolean>;
  /** Restore the original values, both the Form fields and the state living outside it. */
  reset: () => void;
}

/** A leave request waiting for the user to answer the popup. */
export interface PendingLeave {
  changedFields: string[];
  proceed: () => void;
}

type Listener = () => void;

const sources = new Map<string, UnsavedChangesSource>();
const dirtyIds = new Set<string>();
const listeners = new Set<Listener>();

let pendingLeave: PendingLeave | null = null;
let resetVersion = 0;

const notify = () => listeners.forEach((listener) => listener());

/** Register a form into the registry. Returns the unregister function to call on unmount. */
export const register = (source: UnsavedChangesSource): (() => void) => {
  sources.set(source.id, source);
  return () => {
    sources.delete(source.id);
    dirtyIds.delete(source.id);
    notify();
  };
};

/** Collect the changed fields across every mounted form. */
export const getChangedFields = (): string[] => {
  const result: string[] = [];
  sources.forEach((source) => {
    // A form that throws while diffing must not block the remaining ones.
    try {
      result.push(...source.getChangedFields());
    } catch {
      // ignore and treat that form as clean
    }
  });
  return result;
};

/** Lightweight flag used only to place the back/forward sentinel history entry. May be a false positive. */
export const hasDirtyFlag = (): boolean => dirtyIds.size > 0;

export const markDirty = (id: string) => {
  if (dirtyIds.has(id)) return;
  dirtyIds.add(id);
  notify();
};

export const clearDirty = (id: string) => {
  if (!dirtyIds.delete(id)) return;
  notify();
};

/** The request awaiting the user's choice in the popup, or null when there is none. */
export const getPendingLeave = (): PendingLeave | null => pendingLeave;

/**
 * Run the diff and decide: clean means `proceed()` runs right away; dirty parks the request
 * so the Provider can raise the highlight and popup, and `proceed()` only runs once the user
 * chooses to leave.
 *
 * Returns whether `proceed()` already ran, so a caller can undo any optimistic UI it applied
 * before asking (an antd Tabs header keeping focus on the tab that was refused, for instance).
 */
export const requestLeave = (proceed: () => void): boolean => {
  // Ignore new requests while a popup is open so navigations cannot stack up.
  if (pendingLeave) return false;

  const changedFields = getChangedFields();
  if (changedFields.length === 0) {
    proceed();
    return true;
  }

  pendingLeave = { changedFields, proceed };
  notify();
  return false;
};

/** Close the popup and drop the parked navigation. The Provider keeps the highlight on its own. */
export const cancelLeave = () => {
  if (!pendingLeave) return;
  pendingLeave = null;
  notify();
};

/** Perform the parked navigation, after the user chose Exit or a save succeeded. */
export const resolveLeave = () => {
  const pending = pendingLeave;
  if (!pending) return;
  pendingLeave = null;
  notify();
  pending.proceed();
};

/**
 * Counter bumped by every `resetAll`. A page can key its subtree on it so the forms remount and
 * rebuild their local state from the server data: `reset()` restores the Form fields, but it can
 * never reach the `useState` copies a tab keeps of them (a checkbox mirrored into state to hide a
 * section, a derived list, a computed warning), which would otherwise survive Exit Without Saving.
 */
export const getResetVersion = (): number => resetVersion;

/** Restore every form to its original values and clear the dirty flags. */
export const resetAll = () => {
  sources.forEach((source) => source.reset());
  dirtyIds.clear();
  resetVersion += 1;
  notify();
};

/** Save every form holding changes. Returns false if any form fails validation or its API call. */
export const saveAll = async (): Promise<boolean> => {
  const dirtySources = Array.from(sources.values()).filter(
    (source) => source.getChangedFields().length > 0
  );

  for (const source of dirtySources) {
    // Run sequentially so the first failure stops here instead of firing the next mutation.
    const isSaved = await source.save();
    if (!isSaved) return false;
  }
  return true;
};

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
